/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useModal } from '../../contexts/ModalContext'
import { useServiceContext } from '../../root'
import { Select } from '../Select/Select'
import Modal from '../Modal'
import { LoadingModal } from './LoadingModal'
import { usersApiService } from '../../services/api/users'
import { WF_STEP_PROCESSING_TYPE } from '../../constants/workflow-steps'
import type { DetailedWorkflow } from '../../services/api/workflows'

interface WorkflowTraineesModalProps {
  workflowId: number
  workflow?: any
  onSuccess?: () => void
}

interface UserGroupOption {
  value: string
  label: string
  id: number
  systemHelperType: string
}

/**
 * Workflow Trainees (Read & Acknowledge) Management Modal
 *
 * Allows managing trainees/users who need to read and acknowledge workflow.
 * Based on lego/page/workflows/trainees/Modal.jsx
 */
export const WorkflowTraineesModal: React.FC<WorkflowTraineesModalProps> = ({
  workflowId,
  workflow: initialWorkflow,
  onSuccess
}) => {
  const { t } = useTranslation()
  const { hideModal } = useModal()
  const queryClient = useQueryClient()
  const context = useServiceContext()
  const { api } = context

  const [loading, setLoading] = useState(true)
  const [workflow, setWorkflow] = useState<DetailedWorkflow | null>(initialWorkflow || null)
  const [selectedUsersGroups, setSelectedUsersGroups] = useState<string[]>([])
  const [usersGroupsOptions, setUsersGroupsOptions] = useState<UserGroupOption[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    loadData()
  }, [workflowId])

  const loadData = async () => {
    try {
      setLoading(true)

      // Load workflow details if not provided
      let wfData = initialWorkflow
      if (!wfData?.workflowNo) {
        wfData = await api.workflows.getWorkflow(workflowId)
      }

      setWorkflow(wfData)

      // Load users/groups options
      const usersGroupsData = await usersApiService.getAssembledUsersGroups(false)
      const options = usersGroupsData.merged.map((user: any) => ({
        value: user._uid,
        label: user.chName,
        id: user.id,
        systemHelperType: user.systemHelperType
      }))
      setUsersGroupsOptions(options)

      // Load trainees data
      const traineesResponse = await api.workflows.getCRTrainees(wfData.id, true)

      // Transform API response to component format
      // API returns: { trainees: [{ processingType: "...", taskTeams: [{user: {id}}, {group: {id}}] }] }
      // We need to extract taskTeams from steps[0] and convert to "TYPE-id" format
      const steps = traineesResponse.trainees || []
      const taskTeams = steps[0]?.taskTeams || []
      const traineesUsersGroups = taskTeams
        .map((item: any) => {
          if (item.group) {
            return `GROUP-${item.group.id}`
          } else if (item.user) {
            return `USER-${item.user.id}`
          }
          return null
        })
        .filter(Boolean) as string[]

      setSelectedUsersGroups(traineesUsersGroups)
      setLoading(false)
    } catch (error) {
      console.error('Failed to load workflow trainees:', error)
      setErrors({ submit: t('ERROR_LOADING_DATA') || 'Failed to load data' })
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      setIsSubmitting(true)

      // Transform data for API - use steps[0].taskTeams format per DataService transformToServer
      const taskTeams = selectedUsersGroups.map((uid) => {
        const [type, id] = uid.split('-')
        return type === 'USER' ? { user: { id } } : { group: { id } }
      })

      const updateData = {
        trainees: selectedUsersGroups.length
          ? [
              {
                processingType: WF_STEP_PROCESSING_TYPE.PARALLEL,
                taskTeams
              }
            ]
          : []
      }

      await api.workflows.updateCRTrainees(workflowId, updateData, true)

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
      queryClient.invalidateQueries({ queryKey: ['workflow', workflowId] })

      // Show success toast
      context.service.toast.success(t('CHANGE_REQUEST_EDIT_SUCCESS_MSG'))

      hideModal()

      if (onSuccess) {
        onSuccess()
      }
    } catch (error: any) {
      console.error('Failed to update workflow trainees:', error)
      const errorMessage = error?.data?.message || error?.body?.message || t('CHANGE_REQUEST_EDIT_ERROR_MSG')
      context.service.toast.error(errorMessage)
      setErrors({
        submit: errorMessage
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    hideModal()
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleCancel()
    }
  }

  if (loading) {
    return (
      <LoadingModal
        title={t('BROWSER_FILE_SETTINGS_TRAINEES_H_TITLE')}
        onBackdropClick={handleBackdropClick}
        onClose={handleCancel}
      />
    )
  }

  return (
    <Modal show={true} handleClose={handleCancel} title={t('BROWSER_FILE_SETTINGS_TRAINEES_H_TITLE')} size="md">
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          {errors.submit && (
            <div className="alert alert-danger" role="alert">
              {errors.submit}
            </div>
          )}

          <div className="form-group wparag">
            <label className="control-label">{t('WORKFLOW_CHANGE_REQUEST_NUMBER')}</label>
            <p className="whitespaceprewrap">{workflow?.workflowNo}</p>
          </div>

          <div className="form-group">
            <label className="control-label">{t('USERS_GROUPS')}</label>
            <Select
              id="trainees-users-groups"
              name="traineesUsersGroups"
              placeholder={t('SELECT_USERS/GROUPS...')}
              value={selectedUsersGroups}
              options={usersGroupsOptions}
              onChange={(selected: any) => {
                // Extract values from selected options array
                const values = selected ? selected.map((opt: any) => opt.value) : []
                setSelectedUsersGroups(values)
              }}
              isMulti
              isDisabled={isSubmitting}
            />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button type="button" className="btn btn-link" onClick={handleCancel} disabled={isSubmitting}>
            {t('CANCEL')}
          </button>
          <button type="submit" className="btn btn-primary text-capitalize" disabled={isSubmitting}>
            {t('SAVE')}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
